import React, { useState, useEffect, useCallback } from 'react'
import PropTypes, { any } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  useGridApiRef,
  DataGridPremium,
  gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid-premium'
import moment from 'moment'
import { Paper } from '../../../../../components/mui/Core/index'
import LinkButton from '../../../../../components/LinkButton/link-button'
import { getNumberValueFormat } from '../../../../../utils/string-utils'
import { setSelectedInventoryItem } from '../../inventory-slice'
import NoResultsFoundTableOverlay from '../../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'
import Constants from '../../../../../constants/en.json'
import { sessionStorageKeyConstants } from '../../../../../constants/session-storage-key-constants'
import GenericTablePagination from '../../../../../components/GenericTablePagination/generic-table-pagination'
import TableToolBar from '../../../../../components/TableToolBar/table-toolbar'

export function InventoryItemTable({
  filterBy,
  filterValue,
  searchByOptions,
  isIncludeZeroQtyItems = false,
}) {
  const apiRef = useGridApiRef()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  })
  const items = useSelector((state) => state.inventory.items)
  const isFetchingInventoryItems = useSelector((state) => state.inventory.isFetchingInventoryItems)
  const checkboxSelection = false
  const selectedSearchOption = searchByOptions.filter((option) => {
    return option.id === filterBy
  })
  const filterByDescription = selectedSearchOption[0]?.text || ''
  const filterByType = selectedSearchOption[0]?.type
  const noResultsValue = filterByType === 'date' ? moment.utc(filterValue).format('L') : filterValue
  const selectedClient = JSON.parse(
    sessionStorage.getItem(sessionStorageKeyConstants.SELECTED_CLIENT),
  )
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const dmsUser = selectedClient?.dmsFlag || authenticatedUser?.dms

  const handleItemLinkClick = (itemNumber) => {
    dispatch(setSelectedInventoryItem(itemNumber))
    navigate('/itemDetails')
  }

  const rows = items
    .filter((item) =>
      !isIncludeZeroQtyItems ? item.quantity > Constants.inventory.OUT_OF_STOCK_QUANTITY : true,
    )
    .map((row) => ({
      id: row.itemNumber,
      itemNumber: row.itemNumber,
      itemDescription: row.itemDescription,
      liveQuantity: row.liveQuantity,
      quantity: row.quantity,
      uom: row.uom,
      wacListPrice: row.wacListPrice,
    }))

  const [filteredRowsCount, setFilteredRowsCount] = useState(rows.length)

  const updateFilteredRowCount = useCallback(() => {
    const filteredRowIds = gridFilteredSortedRowIdsSelector(apiRef) || []
    setFilteredRowsCount(filteredRowIds?.length || 0)
  }, [apiRef])

  useEffect(() => {
    if (!apiRef.current) return undefined

    updateFilteredRowCount()

    const unsubscribeFilter = apiRef.current.subscribeEvent(
      'filterModelChange',
      updateFilteredRowCount,
    )
    return () => unsubscribeFilter()
  }, [apiRef, rows, updateFilteredRowCount])

  const dmsColumns = [
    {
      field: 'itemNumber',
      headerName: Constants.inventory.NDC_ITEM,
      flex: 0.7,
      minWidth: 150,
      renderCell: (cellValues) => {
        if (cellValues.row?.quantity <= Constants.inventory.OUT_OF_STOCK_QUANTITY) {
          return cellValues.value
        }
        return (
          <LinkButton
            handleLinkClick={() => {
              handleItemLinkClick(cellValues.row)
            }}
            linkText={cellValues.row.itemNumber}
          />
        )
      },
    },
    { field: 'itemDescription', headerName: 'Item Description', flex: 1.5, minWidth: 200 },
    {
      field: 'liveQuantity',
      headerName: Constants.inventory.LIVE_QTY,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => {
        return getNumberValueFormat(value)
      },
      flex: 0.7,
      minWidth: 110,
    },
    { field: 'uom', headerName: 'UOM', minWidth: 120 },
    {
      field: 'quantity',
      headerName: Constants.inventory.TOTAL_QTY,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => {
        return getNumberValueFormat(value)
      },
      flex: 0.6,
      minWidth: 120,
    },
    {
      field: 'wacListPrice',
      headerName: Constants.inventory.WAC_UNIT_PRICE,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => {
        return `$${value}`
      },
      sortComparator: (a, b) => Number(a) - Number(b),
      flex: 0.6,
      minWidth: 150,
    },
  ]

  const wmsColumns = [
    {
      field: 'itemNumber',
      headerName: Constants.inventory.NDC_ITEM,
      flex: 1,
      minWidth: 150,
      renderCell: (cellValues) => {
        if (cellValues.row?.quantity <= Constants.inventory.OUT_OF_STOCK_QUANTITY) {
          return cellValues.value
        }
        return (
          <LinkButton
            handleLinkClick={() => {
              handleItemLinkClick(cellValues.row)
            }}
            linkText={cellValues.row.itemNumber}
          />
        )
      },
    },
    {
      field: 'itemDescription',
      minWidth: 200,
      headerName: Constants.inventory.ITEM_DESCRIPTION,
      flex: 1,
    },
    {
      field: 'liveQuantity',
      minWidth: 150,
      headerName: Constants.inventory.LIVE_QTY,
      headerAlign: 'right',
      valueFormatter: (value) => {
        return getNumberValueFormat(value)
      },
      align: 'right',
      flex: 0.2,
    },
    { field: 'uom', headerName: Constants.inventory.UOM, minWidth: 150 },
    {
      field: 'quantity',
      minWidth: 150,
      headerName: Constants.inventory.TOTAL_QTY,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => {
        return getNumberValueFormat(value)
      },
      flex: 0.2,
    },
  ]

  const filterModel = {
    items: [{ field: filterBy, operator: 'contains', value: filterValue }],
  }

  const inventoryItemTableStyle = {
    minHeight: '160px',
    height: isFetchingInventoryItems ? '400px' : 'auto',
    '& .MuiDataGrid-columnHeader[data-field=uom]': {
      pl: '32px',
    },
    '& .MuiDataGrid-cell[data-field=uom]': {
      pl: '32px',
    },
  }

  const handleChangeRowsPerPage = (e) => {
    setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) })
  }
  const handleChangePage = (e, newPage) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }))
  }

  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      {/* Client pagination */}
      <DataGridPremium
        apiRef={apiRef}
        sx={inventoryItemTableStyle}
        rows={rows}
        columns={dmsUser === 'Y' ? dmsColumns : wmsColumns}
        loading={isFetchingInventoryItems}
        filterModel={filterModel}
        disableColumnFilter
        checkboxSelection={checkboxSelection}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
          sorting: {
            sortModel: [{ field: 'quantity', sort: 'desc' }],
          },
        }}
        sortingOrder={['asc', 'desc']}
        pageSizeOptions={[25, 50]}
        spacing={1}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        slotProps={{
          toolbar: { rows },
          pagination: {
            count: filteredRowsCount,
            page: paginationModel.page,
            rowsPerPage: paginationModel.pageSize,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          },
          noResultsOverlay: {
            filterByDescription,
            noResultsValue,
          },
        }}
        pagination
        slots={{
          noResultsOverlay: NoResultsFoundTableOverlay,
          toolbar: TableToolBar,
          pagination: GenericTablePagination,
        }}
        disableRowSelectionOnClick
        showToolbar
        disableColumnReorder
      />
    </Paper>
  )
}

InventoryItemTable.propTypes = {
  filterBy: PropTypes.string.isRequired,
  filterValue: PropTypes.string.isRequired,
  searchByOptions: PropTypes.arrayOf(PropTypes.objectOf(any)).isRequired,
  isIncludeZeroQtyItems: PropTypes.bool,
}

export default InventoryItemTable
